/**
 * Authentication Context and Provider
 * 
 * Provides centralized authentication state management for the entire application
 * using React Context API. This module handles user authentication, session management,
 * organization data, and related authentication operations.
 * 
 * **Key Features:**
 * - Supabase authentication integration
 * - Automatic session persistence and restoration
 * - Real-time auth state synchronization
 * - User profile and organization data fetching
 * - Email verification management
 * - Secure password and email updates
 * - Role-based access control (client, vendor, admin)
 * 
 * **Authentication Flow:**
 * 1. User signs up with email/password and role
 * 2. Verification email is sent automatically
 * 3. User verifies email via link
 * 4. User can log in with verified credentials
 * 5. Session is maintained across page reloads
 * 6. Profile and organization data is fetched automatically
 * 
 * **Important Implementation Details:**
 * - Uses deferred Supabase calls (setTimeout) to prevent initialization deadlocks
 * - Maintains mounted state to prevent memory leaks
 * - Subscribes to auth state changes for real-time updates
 * - Re-authenticates before sensitive operations (email/password changes)
 * 
 * @module auth
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { User, Session } from '@supabase/supabase-js';

/**
 * User role types for role-based access control
 */
export type UserRole = 'client' | 'vendor' | 'admin';

/**
 * Authenticated user object with profile data
 */
export interface AuthUser {
  /** User's unique ID */
  id: string;
  /** User's email address */
  email: string;
  /** User's role (determines access permissions) */
  role?: UserRole;
  /** User's organization role for permissions */
  orgRole?: 'owner' | 'super_user' | 'member';
  /** ID of the user's organization */
  organization_id?: string;
  /** Timestamp of email verification (null if not verified) */
  email_confirmed_at?: string | null;
}

/**
 * Organization information
 */
export interface Organization {
  /** Organization's unique ID */
  id: string;
  /** Organization display name */
  name: string;
  /** Whether Org Admin features are enabled for this organization */
  org_admin_enabled?: boolean;
}

/**
 * Data required for user registration
 */
export interface SignupData {
  /** User's email address */
  email: string;
  /** User's password (minimum 6 characters) */
  password: string;
  /** Password confirmation (must match password) */
  confirmPassword: string;
  /** User's role selection */
  role: UserRole;
  /** Name of user's organization (optional - defaults to 'My Organization') */
  organization_name?: string;
  /** User must accept terms and conditions */
  acceptTerms: boolean;
}

/**
 * Authentication state shape
 */
interface AuthState {
  /** Currently authenticated user (null if not authenticated) */
  user: AuthUser | null;
  /** User's organization (null if no organization) */
  organization: Organization | null;
  /** Current Supabase session */
  session: Session | null;
  /** Loading state for auth operations */
  isLoading: boolean;
  /** Boolean flag for authentication status */
  isAuthenticated: boolean;
}

/**
 * Complete authentication context interface
 * Extends AuthState with authentication methods
 */
interface AuthContextType extends AuthState {
  /** Sign in with email and password - returns auth data for MFA checking */
  login: (email: string, password: string) => Promise<{ user: any; session: any }>;
  /** Create new user account */
  signup: (data: SignupData) => Promise<void>;
  /** Sign out current user */
  logout: () => Promise<void>;
  /** Refresh user data from database */
  refreshUser: () => Promise<void>;
  /** Update user's email address (requires re-authentication) */
  updateEmail: (newEmail: string, password: string) => Promise<{ success: boolean; error?: string }>;
  /** Update user's password (requires current password) */
  updatePassword: (currentPassword: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  /** Resend email verification link */
  resendVerificationEmail: (email: string) => Promise<void>;
  /** Check if user has MFA enabled and current AAL level */
  checkMFARequired: () => Promise<{ required: boolean; factors: any[]; currentAAL: string }>;
  /** Verify MFA code during login */
  verifyMFA: (factorId: string, code: string) => Promise<void>;
  /** Verify backup code during login */
  verifyMFABackupCode: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Generates a browser fingerprint for session validation.
 * Used to detect when a session is being restored in a different browser context.
 */
const generateSessionFingerprint = (): string => {
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    (navigator as any).deviceMemory || 'unknown',
  ];
  
  // Create a simple hash of the components
  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash.toString(36);
};

/**
 * Logs security events to the security_events table for audit trail.
 * Fails silently to not interrupt authentication flow.
 */
const logSecurityEvent = async (
  userId: string,
  eventType: string,
  details: Record<string, any>
): Promise<void> => {
  try {
    await supabase
      .from('security_events')
      .insert({
        user_id: userId,
        event_type: eventType,
        severity: eventType.includes('blocked') || eventType.includes('mismatch') ? 'high' : 'low',
        details: details,
        ip_address: '0.0.0.0', // Will be captured by server if needed
      });
  } catch (error) {
    // Fail silently - don't interrupt auth flow for logging failures
    console.error('[Auth] Failed to log security event:', error);
  }
};

/**
 * AuthProvider Component
 * 
 * Provides authentication context to all child components. Must wrap the entire
 * application to enable authentication functionality.
 * 
 * This provider automatically:
 * - Initializes auth state on mount
 * - Subscribes to auth state changes
 * - Fetches user profile and organization data
 * - Manages session persistence
 * - Cleans up subscriptions on unmount
 * 
 * @param props - Component props
 * @param props.children - Child components that need access to auth context
 * 
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <BrowserRouter>
 *       <AuthProvider>
 *         <Routes>
 *           <Route path="/" element={<HomePage />} />
 *           <Route path="/dashboard" element={<DashboardPage />} />
 *         </Routes>
 *       </AuthProvider>
 *     </BrowserRouter>
 *   );
 * }
 * ```
 */
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const isAuthenticated = !!session?.user;

  // Initialize auth state
  useEffect(() => {
    let isMounted = true;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        // Handle PASSWORD_RECOVERY event - redirect to reset password page
        // This prevents auto-login when user clicks password reset link
        if (event === 'PASSWORD_RECOVERY') {
          console.log('[AuthProvider] PASSWORD_RECOVERY event detected - redirecting to reset password page');
          setSession(session);
          setIsLoading(false);
          // Navigate to reset password page with the session intact
          navigate('/auth/reset-password');
          return;
        }
        
        setSession(session);
        
        if (session?.user) {
          // Defer Supabase calls to prevent deadlock
          setTimeout(() => {
            if (!isMounted) return;
            
            const fetchUserData = async () => {
              try {
                // Check if session requires MFA verification using proper Supabase API
                const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
                const currentAAL = aalData?.currentLevel || 'aal1';
                
                console.log('[AuthProvider] Checking MFA requirement in onAuthStateChange:', {
                  event,
                  userId: session.user.id,
                  currentAAL,
                  nextLevel: aalData?.nextLevel
                });
                
                // Check MFA factors to determine if we should wait for MFA
                const { data: mfaData } = await supabase.auth.mfa.listFactors();
                const hasMFAFactors = (mfaData?.totp || []).length > 0;
                
                console.log('[AuthProvider] MFA factors check:', {
                  hasMFAFactors,
                  factorCount: (mfaData?.totp || []).length,
                  shouldWaitForMFA: hasMFAFactors && currentAAL === 'aal1'
                });
                
                // If user has MFA enabled but session is only aal1, don't proceed with full login
                // UNLESS the event is MFA_CHALLENGE_VERIFIED or TOKEN_REFRESHED (after MFA)
                // The login page will handle showing the MFA verification UI for initial login
                const isMFAVerificationEvent = event === 'MFA_CHALLENGE_VERIFIED' || 
                  (event === 'TOKEN_REFRESHED' && hasMFAFactors);
                
                if (hasMFAFactors && currentAAL === 'aal1' && !isMFAVerificationEvent) {
                  console.log('[AuthProvider] Session requires MFA verification - waiting for 2FA');
                  setIsLoading(false);
                  return;
                }
                
                console.log('[AuthProvider] Proceeding with user data fetch after MFA verification');
                
                // Fetch role and suspension status from secure user_roles table
                const { data: roleData } = await supabase
                  .from('user_roles' as any)
                  .select('role, is_suspended')
                  .eq('user_id', session.user.id)
                  .order('assigned_at', { ascending: false })
                  .limit(1)
                  .maybeSingle();

                // Check if user account is suspended
                if ((roleData as any)?.is_suspended) {
                  console.warn('[Auth] User account is suspended, logging out user');
                  await supabase.auth.signOut();
                  setUser(null);
                  setOrganization(null);
                  setIsLoading(false);
                  return;
                }

                // Debug logging for role loading failures
                const userRole = (roleData as any)?.role;
                if (!userRole) {
                  console.error('[Auth] Role not found for user:', {
                    userId: session.user.id,
                    email: session.user.email,
                    roleData,
                    timestamp: new Date().toISOString()
                  });
                }

                // Fetch profile data
                const { data: profile } = await supabase
                  .from('profiles')
                  .select('organization_id')
                  .eq('user_id', session.user.id)
                  .maybeSingle();

                if (!isMounted) return;

              // Fetch org role if user has organization
              let orgRole: 'owner' | 'super_user' | 'member' | undefined;
              if (profile?.organization_id) {
                const { data: orgRoleData, error: orgRoleError } = await supabase
                  .rpc('get_user_org_role', {
                    _user_id: session.user.id,
                    _org_id: profile.organization_id
                  } as any);
                orgRole = orgRoleData as any;
                
                // Debug logging for org role issues
                if (!orgRole) {
                  console.warn('[Auth] No org role found for user:', {
                    userId: session.user.id,
                    organizationId: profile.organization_id,
                    error: orgRoleError
                  });
                }
              }

                setUser({
                  id: session.user.id,
                  email: session.user.email || '',
                  role: (roleData as any)?.role as UserRole,
                  orgRole,
                  organization_id: profile?.organization_id,
                  email_confirmed_at: session.user.email_confirmed_at,
                });

                // Fetch organization data
                if (profile?.organization_id) {
                  const { data: orgData } = await supabase
                    .from('organizations')
                    .select('id, name, status, org_admin_enabled')
                    .eq('id', profile.organization_id)
                    .maybeSingle();

                  if (!isMounted) return;

                  if (orgData) {
                    // Check if organization is suspended
                    if (orgData.status === 'suspended') {
                      console.warn('[Auth] Organization is suspended, logging out user');
                      await supabase.auth.signOut();
                      setUser(null);
                      setOrganization(null);
                      setIsLoading(false);
                      return;
                    }

                    setOrganization({
                      id: orgData.id,
                      name: orgData.name,
                      org_admin_enabled: orgData.org_admin_enabled ?? false,
                    });
                  }
                }
              } catch (error) {
                console.error('Error fetching user profile:', error);
                if (isMounted) {
                  setUser(null);
                  setOrganization(null);
                }
              } finally {
                if (isMounted) {
                  setIsLoading(false);
                }
              }
            };
            
            fetchUserData();
          }, 0);
        } else {
          setUser(null);
          setOrganization(null);
          setIsLoading(false);
        }
      }
    );

    // Check for existing session with ENHANCED SECURITY CHECKS
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!isMounted) return;
      
      // Always set the session from current state
      setSession(session);

      if (session?.user) {
        // Defer Supabase calls to prevent any potential deadlocks
        setTimeout(() => {
          if (!isMounted) return;

          const initUserData = async () => {
            try {
              // ========== SECURITY CHECK 1: MFA VERIFICATION ==========
              // Same MFA check that exists in onAuthStateChange - prevents session restoration
              // without completing MFA when user has MFA factors enabled
              // Use proper Supabase API to get AAL level - session.aal is unreliable after restore
              const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
              const currentAAL = aalData?.currentLevel || 'aal1';
              const { data: mfaData } = await supabase.auth.mfa.listFactors();
              const hasMFAFactors = (mfaData?.totp || []).length > 0;
              
              console.log('[Auth] Session restore security check:', {
                userId: session.user.id,
                email: session.user.email,
                currentAAL,
                nextLevel: aalData?.nextLevel,
                hasMFAFactors,
                sessionCreatedAt: session.expires_at
              });
              
              // If user has MFA enabled but session is only aal1, force re-login
              if (hasMFAFactors && currentAAL === 'aal1') {
                console.warn('[Auth] SECURITY: Session requires MFA re-verification on restore - forcing logout');
                
                // Log security event for audit trail
                await logSecurityEvent(session.user.id, 'session_restore_blocked_mfa', {
                  reason: 'MFA required but session at aal1',
                  userAgent: navigator.userAgent,
                  timestamp: new Date().toISOString()
                });
                
                await supabase.auth.signOut();
                setUser(null);
                setOrganization(null);
                setSession(null);
                setIsLoading(false);
                return;
              }
              
              // ========== SECURITY CHECK 2: SESSION AGE FOR ADMIN USERS ==========
              // Fetch role and suspension status first
              const { data: roleData } = await supabase
                .from('user_roles' as any)
                .select('role, is_suspended')
                .eq('user_id', session.user.id)
                .order('assigned_at', { ascending: false })
                .limit(1)
                .maybeSingle();
              
              // Check if user account is suspended
              if ((roleData as any)?.is_suspended) {
                console.warn('[Auth] SECURITY: User account is suspended - forcing logout');
                await supabase.auth.signOut();
                setUser(null);
                setOrganization(null);
                setSession(null);
                setIsLoading(false);
                return;
              }
              
              const userRole = (roleData as any)?.role as UserRole;
              
              // Admin users have shorter session timeout (2 hours)
              if (userRole === 'admin') {
                const sessionExpiresAt = session.expires_at ? new Date(session.expires_at * 1000) : null;
                const sessionCreatedAt = sessionExpiresAt 
                  ? new Date(sessionExpiresAt.getTime() - (3600 * 1000)) // Estimate creation as 1 hour before expiry
                  : null;
                const now = new Date();
                const adminSessionMaxAge = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
                
                // Check if session is older than admin max age
                // Use access_token creation time if available
                const tokenIssuedAt = (session as any).token_created_at 
                  ? new Date((session as any).token_created_at * 1000)
                  : sessionCreatedAt;
                
                if (tokenIssuedAt && (now.getTime() - tokenIssuedAt.getTime()) > adminSessionMaxAge) {
                  console.warn('[Auth] SECURITY: Admin session expired due to age - forcing logout');
                  
                  await logSecurityEvent(session.user.id, 'admin_session_expired', {
                    reason: 'Admin session exceeded 2 hour limit',
                    sessionAge: now.getTime() - tokenIssuedAt.getTime(),
                    userAgent: navigator.userAgent,
                    timestamp: new Date().toISOString()
                  });
                  
                  await supabase.auth.signOut();
                  setUser(null);
                  setOrganization(null);
                  setSession(null);
                  setIsLoading(false);
                  return;
                }
              }
              
              // ========== SECURITY CHECK 3: SESSION FINGERPRINT VALIDATION ==========
              const currentFingerprint = generateSessionFingerprint();
              const storedFingerprint = localStorage.getItem(`session_fingerprint_${session.user.id}`);
              
              if (storedFingerprint && storedFingerprint !== currentFingerprint) {
                console.warn('[Auth] SECURITY: Session fingerprint mismatch detected');
                
                await logSecurityEvent(session.user.id, 'session_fingerprint_mismatch', {
                  reason: 'Browser/context fingerprint changed',
                  storedFingerprint: storedFingerprint.substring(0, 8) + '...',
                  currentFingerprint: currentFingerprint.substring(0, 8) + '...',
                  userAgent: navigator.userAgent,
                  timestamp: new Date().toISOString()
                });
                
                // For admin users, force re-login on fingerprint mismatch
                if (userRole === 'admin') {
                  console.warn('[Auth] SECURITY: Admin session invalidated due to fingerprint mismatch');
                  await supabase.auth.signOut();
                  setUser(null);
                  setOrganization(null);
                  setSession(null);
                  setIsLoading(false);
                  return;
                }
                // For non-admin users, log but allow (could be legitimate browser update)
              }
              
              // Store/update fingerprint for this session
              localStorage.setItem(`session_fingerprint_${session.user.id}`, currentFingerprint);
              
              // Log successful session restoration for audit
              await logSecurityEvent(session.user.id, 'session_restored', {
                role: userRole,
                currentAAL,
                userAgent: navigator.userAgent,
                timestamp: new Date().toISOString()
              });

              // Fetch profile data
              const { data: profile } = await supabase
                .from('profiles')
                .select('organization_id')
                .eq('user_id', session.user.id)
                .maybeSingle();

              if (!isMounted) return;

              // Fetch org role if user has organization
              let orgRole: 'owner' | 'super_user' | 'member' | undefined;
              if (profile?.organization_id) {
                const { data: orgRoleData, error: orgRoleError } = await supabase
                  .rpc('get_user_org_role', {
                    _user_id: session.user.id,
                    _org_id: profile.organization_id
                  } as any);
                orgRole = orgRoleData as any;
                
                // Debug logging for org role issues
                if (!orgRole) {
                  console.warn('[Auth] No org role found for user:', {
                    userId: session.user.id,
                    organizationId: profile.organization_id,
                    error: orgRoleError
                  });
                }
              }

              setUser({
                id: session.user.id,
                email: session.user.email || '',
                role: userRole,
                orgRole,
                organization_id: profile?.organization_id,
                email_confirmed_at: session.user.email_confirmed_at,
              });

              // Fetch organization data
              if (profile?.organization_id) {
                const { data: orgData } = await supabase
                  .from('organizations')
                  .select('id, name, status, org_admin_enabled')
                  .eq('id', profile.organization_id)
                  .maybeSingle();

                if (!isMounted) return;

                if (orgData) {
                  // Check if organization is suspended
                  if (orgData.status === 'suspended') {
                    console.warn('[Auth] Organization is suspended during session check, logging out user');
                    await supabase.auth.signOut();
                    setUser(null);
                    setOrganization(null);
                    setIsLoading(false);
                    return;
                  }

                  setOrganization({
                    id: orgData.id,
                    name: orgData.name,
                    org_admin_enabled: orgData.org_admin_enabled ?? false,
                  });
                }
              }
            } catch (error) {
              console.error('Error initializing user from existing session:', error);
              if (isMounted) {
                setUser(null);
                setOrganization(null);
              }
            } finally {
              if (isMounted) {
                setIsLoading(false);
              }
            }
          };

          initUserData();
        }, 0);
      } else {
        setUser(null);
        setOrganization(null);
        setIsLoading(false);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      console.log('[Login] signInWithPassword result:', {
        hasSession: !!data.session,
        hasUser: !!data.user,
        aal: (data.session as any)?.aal,
        sessionData: data.session
      });

      if (error) {
        console.error('Login error details:', {
          message: error.message,
          name: error.name,
          status: error.status,
          code: (error as any).code,
          fullError: error
        });
        
        // Check for unconfirmed email error
        if (
          error.message?.toLowerCase().includes('email not confirmed') ||
          error.message?.toLowerCase().includes('email_not_confirmed') ||
          (error as any)?.code === 'email_not_confirmed'
        ) {
          throw new Error('Please verify your email address before logging in. Check your inbox for the verification link.');
        }
        throw error;
      }

      // Check if user account is suspended
      if (data.user) {
        const { data: userRoleData } = await supabase
          .from('user_roles' as any)
          .select('is_suspended')
          .eq('user_id', data.user.id)
          .maybeSingle();

        if ((userRoleData as any)?.is_suspended) {
          await supabase.auth.signOut();
          throw new Error('Your account has been suspended. Please contact support for assistance.');
        }

        // Check if user's organization is suspended
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('user_id', data.user.id)
          .maybeSingle();

        if (profile?.organization_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('status')
            .eq('id', profile.organization_id)
            .maybeSingle();

          if (org?.status === 'suspended') {
            // Log the user out immediately
            await supabase.auth.signOut();
            throw new Error('Your organization has been suspended. Please contact support for assistance.');
          }
        }
      }
      
      // Return session data - let caller handle navigation based on MFA requirements
      // Store session fingerprint for security validation on future session restores
      if (data.user) {
        const fingerprint = generateSessionFingerprint();
        localStorage.setItem(`session_fingerprint_${data.user.id}`, fingerprint);
        
        // Log successful login for audit trail
        await logSecurityEvent(data.user.id, 'login_success', {
          email: data.user.email,
          method: 'password',
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        });
      }
      
      return data;
    } catch (error) {
      throw error;
    }
  };

  const signup = async (data: SignupData) => {
    // NOTE: We intentionally do NOT use setIsLoading(true/false) here.
    // The signup page has its own local isSubmitting state for the button.
    // Using global isLoading causes the full-page loader to mount, which
    // unmounts the signup form and prevents error messages from displaying.
    try {
      // PRE-CHECK: Verify email is not already registered
      // This prevents the race condition where Supabase's "obfuscated user" response
      // triggers auth state changes before we can detect the duplicate email
      const checkResponse = await invokeEdgeFunction('check-email-exists', {
        body: { email: data.email },
        callerOperation: 'AuthProvider.signup.checkEmail',
      });
      
      if (checkResponse.error) {
        console.error('[Signup] Error checking email existence:', checkResponse.error);
        // Continue with signup attempt if check fails - let Supabase handle it
      } else if ((checkResponse.data as { exists?: boolean })?.exists) {
        throw new Error(
          'An account with this email already exists. Please try logging in instead, or use the "Forgot Password" option if you need to recover your account.'
        );
      }

      const { data: signupData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`,
          data: {
            role: data.role,
            organization_name: data.organization_name,
          },
        },
      });

      if (error) throw error;
      
      // Fallback: Detect if email already exists (Supabase returns empty identities array)
      if (signupData?.user?.identities?.length === 0) {
        throw new Error(
          'An account with this email may already exist. Please try logging in instead, or use the "Forgot Password" option if you need to recover your account.'
        );
      }
      
      // Don't navigate to dashboard - signup component will handle redirect
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    // Clear session fingerprint on logout
    if (user?.id) {
      localStorage.removeItem(`session_fingerprint_${user.id}`);
      
      // Log logout for audit trail
      await logSecurityEvent(user.id, 'logout', {
        email: user.email,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    }
    
    await supabase.auth.signOut();
    navigate('/auth/login');
  };

  const refreshUser = async () => {
    if (session?.user) {
      // Fetch role from secure user_roles table
      const { data: roleData } = await supabase
        .from('user_roles' as any)
        .select('role')
        .eq('user_id', session.user.id)
        .order('assigned_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // Fetch profile data
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', session.user.id)
        .maybeSingle();

      setUser({
        id: session.user.id,
        email: session.user.email || '',
        role: (roleData as any)?.role as UserRole,
        organization_id: profile?.organization_id,
        email_confirmed_at: session.user.email_confirmed_at,
      });

      // Fetch organization data
      if (profile?.organization_id) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('id, name, org_admin_enabled')
          .eq('id', profile.organization_id)
          .maybeSingle();

        if (orgData) {
          setOrganization({
            id: orgData.id,
            name: orgData.name,
            org_admin_enabled: orgData.org_admin_enabled ?? false,
          });
        }
      }
    }
  };

  const updateEmail = async (newEmail: string, password: string) => {
    try {
      if (!user?.email) {
        return { success: false, error: 'No user email found' };
      }

      // Re-authenticate to verify password
      const { error: reAuthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password,
      });

      if (reAuthError) {
        return { success: false, error: 'Incorrect password' };
      }

      // Update email (triggers verification email)
      const { error } = await supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) {
        // Handle specific error cases
        if (error.message.includes('already registered')) {
          return { success: false, error: 'This email is already registered' };
        }
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  };

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      if (!user?.email) {
        return { success: false, error: 'No user email found' };
      }

      // Re-authenticate to verify current password
      const { error: reAuthError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: currentPassword,
      });

      if (reAuthError) {
        return { success: false, error: 'Current password is incorrect' };
      }

      // Update password
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        // Handle specific error cases
        if (error.message.includes('same as the old password')) {
          return { success: false, error: 'New password must be different from your current password' };
        }
        if (error.message.includes('Password should be')) {
          return { success: false, error: 'Password must be at least 6 characters' };
        }
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'An unexpected error occurred' };
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/confirm`
        }
      });
      
      if (error) throw error;
    } catch (error) {
      throw error;
    }
  };

  const checkMFARequired = async () => {
    try {
      // Get current session to check AAL level
      const { data: { session: currentSession }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[MFA Check] Error getting session:', sessionError);
        throw sessionError;
      }
      
      console.log('[MFA Check] Current session state:', {
        hasSession: !!currentSession,
        userId: currentSession?.user?.id,
        aal: (currentSession as any)?.aal,
        fullSession: currentSession
      });
      
      // Get MFA factors
      const { data, error } = await supabase.auth.mfa.listFactors();
      
      if (error) {
        console.error('[MFA Check] Error listing factors:', error);
        throw error;
      }
      
      const totpFactors = data?.totp || [];
      console.log('[MFA Check] MFA factors found:', {
        totpFactorCount: totpFactors.length,
        factorIds: totpFactors.map(f => f.id),
        factorStatuses: totpFactors.map(f => f.status)
      });
      
      // Use proper Supabase API to get AAL level - session.aal is unreliable after restore
      const { data: aalData } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      const currentAAL = aalData?.currentLevel || 'aal1';
      
      // MFA is required if:
      // 1. User has verified MFA factors AND
      // 2. Current session is at aal1 (password-only, not yet MFA verified)
      const verifiedFactors = totpFactors.filter(f => f.status === 'verified');
      const mfaRequired = verifiedFactors.length > 0 && currentAAL === 'aal1';
      
      console.log('[MFA Check] Final determination:', {
        mfaRequired,
        verifiedFactorCount: verifiedFactors.length,
        totalFactorCount: totpFactors.length,
        currentAAL,
        nextLevel: aalData?.nextLevel,
        logic: `${verifiedFactors.length} verified factors > 0 && ${currentAAL} === 'aal1' = ${mfaRequired}`
      });
      
      return {
        required: mfaRequired,
        factors: verifiedFactors, // Only return verified factors
        currentAAL,
      };
    } catch (error) {
      console.error('[MFA Check] Error checking MFA status:', error);
      return { required: false, factors: [], currentAAL: 'aal1' };
    }
  };

  const verifyMFA = async (factorId: string, code: string) => {
    try {
      console.log('[Auth] Starting MFA verification...');
      
      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) {
        console.error('[Auth] MFA challenge error:', challengeError);
        throw challengeError;
      }

      // Verify the code
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) {
        console.error('[Auth] MFA verify error:', verifyError);
        throw verifyError;
      }

      console.log('[Auth] MFA verify successful, refreshing session...');

      // CRITICAL: Use refreshSession to force Supabase to return updated session
      const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.error('[Auth] Session refresh error:', refreshError);
      }
      
      if (newSession) {
        console.log('[Auth] New session after MFA:', {
          aal: (newSession as any).aal,
          userId: newSession.user.id
        });
        
        setSession(newSession);
        
        // Verify we're at aal2 now (use type assertion)
        if ((newSession as any).aal === 'aal2') {
          console.log('[Auth] MFA verification successful - session upgraded to aal2');
        } else {
          console.warn('[Auth] Session AAL is not aal2 after verification:', (newSession as any).aal);
        }
      } else {
        console.error('[Auth] No session returned after MFA verification');
      }
    } catch (error) {
      console.error('[Auth] MFA verification failed:', error);
      throw error;
    }
  };

  const verifyMFABackupCode = async (code: string) => {
    try {
      const userId = user?.id;
      if (!userId) throw new Error('User not authenticated');

      // Hash the code
      const encoder = new TextEncoder();
      const data = encoder.encode(code.replace(/-/g, '').toUpperCase());
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const codeHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      // Verify backup code
      const { data: isValid, error } = await supabase.rpc('verify_mfa_backup_code', {
        p_user_id: userId,
        p_code_hash: codeHash,
      });

      if (error) throw error;
      if (!isValid) throw new Error('Invalid or already used backup code');

      // Get remaining codes count
      const { count } = await supabase
        .from('mfa_backup_codes')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .is('used_at', null);

      // Send notification
      await supabase.functions.invoke('mfa-notifications', {
        body: {
          type: 'backup_code_used',
          userId,
          details: { remaining: count || 0 },
        },
      });

      // Refresh session - should be upgraded to aal2 after backup code verification
      const { data: { session: newSession } } = await supabase.auth.getSession();
      if (newSession) {
        setSession(newSession);
        
        // Verify we're at aal2 now (use type assertion)
        if ((newSession as any).aal === 'aal2') {
          console.log('Backup code verification successful - session upgraded to aal2');
        }
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        organization,
        session,
        isLoading,
        isAuthenticated,
        login,
        signup,
        logout,
        refreshUser,
        updateEmail,
        updatePassword,
        resendVerificationEmail,
        checkMFARequired,
        verifyMFA,
        verifyMFABackupCode,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth Hook
 * 
 * Custom hook to access authentication context. Must be used within an AuthProvider.
 * Provides access to authentication state and methods throughout the application.
 * 
 * @returns Authentication context with user state and auth methods
 * @throws Error if used outside of AuthProvider
 * 
 * @example
 * ```tsx
 * function ProfilePage() {
 *   const { user, isAuthenticated, logout } = useAuth();
 *   
 *   if (!isAuthenticated) {
 *     return <Navigate to="/auth/login" />;
 *   }
 *   
 *   return (
 *     <div>
 *       <h1>Welcome, {user?.email}</h1>
 *       <p>Role: {user?.role}</p>
 *       <button onClick={logout}>Sign Out</button>
 *     </div>
 *   );
 * }
 * ```
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};